import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  Space, 
  Modal, 
  Descriptions,
  message,
  Spin,
  Pagination,
  Form,
  InputNumber
} from 'antd';
import { 
  SearchOutlined, 
  EyeOutlined, 
  DownloadOutlined, 
  FilterOutlined,
  DollarOutlined,
  UserOutlined,
  CalendarOutlined,
  UndoOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import apiClient from '../../services/apiClient';
import AdminPaymentService from '../../services/adminPaymentService';

const { RangePicker } = DatePicker;
const { Option } = Select;

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundDetailsModalVisible, setRefundDetailsModalVisible] = useState(false);
  const [refundDetails, setRefundDetails] = useState(null);
  const [refundDetailsLoading, setRefundDetailsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    startDate: null,
    endDate: null
  });
  const [refundForm] = Form.useForm();

  useEffect(() => {
    fetchPaymentsAndStats();
  }, [pagination.current, pagination.pageSize]);

  const fetchPaymentsAndStats = async () => {
    setLoading(true);
    try {
      // Single API call to fetch all payments
      const response = await apiClient.get('http://localhost:8080/api/v1/sb/razorpay/payments', {
        params: {
          count: 100, // Get more data for client-side filtering and stats
          skip: 0
        }
      });
      const allPayments = response.data || [];
      
      // Calculate statistics from all payments
      const totalRevenue = allPayments.reduce((sum, payment) => {
        return sum + (payment.amount || 0);
      }, 0);
      
      const paymentsByStatus = allPayments.reduce((acc, payment) => {
        const status = payment.status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      
      const recentPayments = allPayments.filter(payment => {
        const paymentDate = new Date(payment.created_at * 1000); // Razorpay uses Unix timestamp
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return paymentDate >= thirtyDaysAgo;
      }).length;
      
      // Update statistics
      setStats({
        totalRevenue: totalRevenue / 100, // Convert from paise to rupees
        totalPayments: allPayments.length,
        recentPayments,
        paymentsByStatus
      });
      
      // Apply client-side filtering for table
      let filteredPayments = allPayments;
      
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredPayments = filteredPayments.filter(payment => 
          payment.email?.toLowerCase().includes(searchTerm) ||
          payment.id?.toLowerCase().includes(searchTerm) ||
          payment.order_id?.toLowerCase().includes(searchTerm)
        );
      }
      
      if (filters.status) {
        filteredPayments = filteredPayments.filter(payment => 
          payment.status === filters.status
        );
      }
      
      if (filters.startDate && filters.endDate) {
        filteredPayments = filteredPayments.filter(payment => {
          const paymentDate = new Date(payment.created_at * 1000);
          const startDate = new Date(filters.startDate);
          const endDate = new Date(filters.endDate);
          return paymentDate >= startDate && paymentDate <= endDate;
        });
      }
      
      // Apply pagination to filtered results
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const paginatedPayments = filteredPayments.slice(startIndex, endIndex);
      
      setPayments(paginatedPayments);
      setPagination(prev => ({
        ...prev,
        total: filteredPayments.length
      }));
    } catch (error) {
      message.error('Failed to fetch payments');
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setModalVisible(true);
  };

  const handleRefundPayment = (payment) => {
    setSelectedPayment(payment);
    setRefundModalVisible(true);
    // Pre-fill the form with payment amount
    refundForm.setFieldsValue({
      amount: payment.amount,
      speed: 'normal'
    });
  };

  const handleRefundSubmit = async (values) => {
    if (!selectedPayment) return;
    
    setRefundLoading(true);
    try {
      const refundData = {
        amount: values.amount,
        speed: values.speed,
        notes: values.notes ? { reason: values.notes } : undefined,
        receipt: values.receipt
      };
      
      const response = await AdminPaymentService.refundPayment(selectedPayment.id, refundData);
      message.success('Refund initiated successfully');
      setRefundModalVisible(false);
      refundForm.resetFields();
      // Refresh the payments list
      fetchPaymentsAndStats();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to process refund');
      console.error('Refund error:', error);
    } finally {
      setRefundLoading(false);
    }
  };

  const canRefundPayment = (payment) => {
    // Only allow refund for captured payments
    return payment.status === 'captured' && !payment.refund_status;
  };

  const handleRefundDetails = async (payment) => {
    setSelectedPayment(payment);
    setRefundDetailsModalVisible(true);
    setRefundDetailsLoading(true);
    
    try {
      // Try to get refund details using the payment's refund_id if available
      // or fetch all refunds and find the one for this payment
      let refundData = null;
      
      if (payment.refund_id) {
        // If payment has refund_id, fetch specific refund
        refundData = await AdminPaymentService.getRefundDetails(payment.refund_id);
      } else {
        // Otherwise, fetch all refunds and find the one for this payment
        const allRefunds = await AdminPaymentService.getAllRefunds(100, 0);
        refundData = allRefunds.find(refund => refund.payment_id === payment.id);
      }
      
      setRefundDetails(refundData);
    } catch (error) {
      message.error('Failed to fetch refund details');
      console.error('Error fetching refund details:', error);
    } finally {
      setRefundDetailsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'captured':
        return 'green';
      case 'authorized':
        return 'blue';
      case 'created':
        return 'blue';
      case 'failed':
        return 'red';
      case 'cancelled':
        return 'default';
      case 'refunded':
        return 'purple';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Payment ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      render: (id) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {id}
        </span>
      ),
    },
    {
      title: 'Customer Email',
      dataIndex: 'email',
      key: 'email',
      width: 170,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (amount, record) => (
        <span>₹{(amount / 100).toFixed(2)} {record.currency}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 80,
      render: (method) => (
        <Tag color="blue">{method?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (timestamp) => moment.unix(timestamp).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => handleViewPayment(record)}
          >
            View
          </Button>
          {canRefundPayment(record) && (
            <Button 
              type="link" 
              icon={<UndoOutlined />} 
              onClick={() => handleRefundPayment(record)}
              style={{ color: '#ff4d4f' }}
            >
              Refund
            </Button>
          )}
          {record.status === 'refunded' && (
            <Button 
              type="link" 
              icon={<InfoCircleOutlined />} 
              onClick={() => handleRefundDetails(record)}
              style={{ color: '#1890ff' }}
            >
              Refund Details
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
    // Trigger refetch with new filters
    fetchPaymentsAndStats();
  };

  const handleSearch = (value) => {
    handleFilterChange('search', value);
  };

  return (
    <div style={{ padding: '24px' }}>
      <h2>Payment Management</h2>
      
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
      <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={stats.totalRevenue || 0}
              prefix="₹"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Total Payments"
              value={stats.totalPayments || 0}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Recent (30 days)"
              value={stats.recentPayments || 0}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
       
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Captured"
              value={stats.paymentsByStatus?.captured || 0}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Authorized"
              value={stats.paymentsByStatus?.authorized || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Failed"
              value={stats.paymentsByStatus?.failed || 0}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Search by Email, Payment ID, or Order ID"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onPressEnter={(e) => handleSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="">All</Option>
              <Option value="captured">Captured</Option>
              <Option value="authorized">Authorized</Option>
              <Option value="created">Created</Option>
              <Option value="failed">Failed</Option>
              <Option value="cancelled">Cancelled</Option>
              <Option value="refunded">Refunded</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => {
                if (dates) {
                  handleFilterChange('startDate', dates[0]?.format('YYYY-MM-DDTHH:mm:ss'));
                  handleFilterChange('endDate', dates[1]?.format('YYYY-MM-DDTHH:mm:ss'));
                } else {
                  handleFilterChange('startDate', null);
                  handleFilterChange('endDate', null);
                }
              }}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Button 
              type="primary" 
              icon={<FilterOutlined />}
              onClick={fetchPaymentsAndStats}
            >
              Apply Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Payments Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={payments}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} payments`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Payment Details Modal */}
      <Modal
        title="Payment Details"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedPayment && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Payment ID" span={2}>
              <span style={{ fontFamily: 'monospace' }}>{selectedPayment.id}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Customer Email">
              {selectedPayment.email}
            </Descriptions.Item>
            <Descriptions.Item label="Contact">
              {selectedPayment.contact || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              ₹{(selectedPayment.amount / 100).toFixed(2)} {selectedPayment.currency}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedPayment.status)}>
                {selectedPayment.status?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Method">
              {selectedPayment.method?.toUpperCase()}
            </Descriptions.Item>
            <Descriptions.Item label="Order ID">
              {selectedPayment.order_id || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Fee">
              ₹{selectedPayment.fee ? (selectedPayment.fee / 100).toFixed(2) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {moment.unix(selectedPayment.created_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Captured">
              {selectedPayment.captured ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="International">
              {selectedPayment.international ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {selectedPayment.description || 'N/A'}
            </Descriptions.Item>
            {selectedPayment.card && (
              <>
                <Descriptions.Item label="Card Type">
                  {selectedPayment.card.type?.toUpperCase()}
                </Descriptions.Item>
                <Descriptions.Item label="Card Last 4">
                  ****{selectedPayment.card.last4}
                </Descriptions.Item>
                <Descriptions.Item label="Card Network">
                  {selectedPayment.card.network?.toUpperCase()}
                </Descriptions.Item>
                <Descriptions.Item label="Card Issuer">
                  {selectedPayment.card.issuer}
                </Descriptions.Item>
              </>
            )}
            {selectedPayment.upi && (
              <Descriptions.Item label="UPI VPA">
                {selectedPayment.upi.vpa}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Refund Modal */}
      <Modal
        title="Refund Payment"
        visible={refundModalVisible}
        onCancel={() => {
          setRefundModalVisible(false);
          refundForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        {selectedPayment && (
          <div>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <strong>Payment Details:</strong><br />
              Payment ID: {selectedPayment.id}<br />
              Amount: ₹{(selectedPayment.amount / 100).toFixed(2)}<br />
              Customer: {selectedPayment.email}
            </div>
            
            <Form
              form={refundForm}
              layout="vertical"
              onFinish={handleRefundSubmit}
            >
              <Form.Item
                label="Refund Amount (in paise)"
                name="amount"
                rules={[{ required: true, message: 'Please enter refund amount' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter amount in paise"
                  min={1}
                  max={selectedPayment.amount}
                />
              </Form.Item>
              
              <Form.Item
                label="Refund Speed"
                name="speed"
                rules={[{ required: true, message: 'Please select refund speed' }]}
              >
                <Select placeholder="Select refund speed">
                  <Option value="normal">Normal (5-10 business days)</Option>
                  <Option value="optimum">Optimum (3-5 business days)</Option>
                </Select>
              </Form.Item>
              
              <Form.Item
                label="Refund Notes"
                name="notes"
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Enter reason for refund (optional)"
                />
              </Form.Item>
              
              <Form.Item
                label="Receipt Number"
                name="receipt"
              >
                <Input placeholder="Enter receipt number (optional)" />
              </Form.Item>
              
              <Form.Item style={{ marginBottom: 0 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => {
                    setRefundModalVisible(false);
                    refundForm.resetFields();
                  }}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={refundLoading}>
                    Process Refund
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Refund Details Modal */}
      <Modal
        title="Refund Details"
        visible={refundDetailsModalVisible}
        onCancel={() => {
          setRefundDetailsModalVisible(false);
          setRefundDetails(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setRefundDetailsModalVisible(false);
            setRefundDetails(null);
          }}>
            Close
          </Button>
        ]}
        width={800}
      >
        {refundDetailsLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <p style={{ marginTop: '16px' }}>Loading refund details...</p>
          </div>
        ) : refundDetails ? (
          <div>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <strong>Payment Information:</strong><br />
              Payment ID: {selectedPayment?.id}<br />
              Original Amount: ₹{(selectedPayment?.amount / 100).toFixed(2)}<br />
              Customer: {selectedPayment?.email}
            </div>
            
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Refund ID" span={2}>
                <span style={{ fontFamily: 'monospace' }}>{refundDetails.id}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Refund Amount">
                ₹{(refundDetails.amount / 100).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={refundDetails.status === 'processed' ? 'green' : 'orange'}>
                  {refundDetails.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Speed">
                {refundDetails.speed?.toUpperCase() || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Receipt">
                {refundDetails.receipt || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {moment.unix(refundDetails.created_at).format('DD/MM/YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Processed At">
                {refundDetails.processed_at ? moment.unix(refundDetails.processed_at).format('DD/MM/YYYY HH:mm:ss') : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Acquirer Data" span={2}>
                {refundDetails.acquirer_data ? JSON.stringify(refundDetails.acquirer_data, null, 2) : 'N/A'}
              </Descriptions.Item>
              {refundDetails.notes && Object.keys(refundDetails.notes).length > 0 && (
                <Descriptions.Item label="Notes" span={2}>
                  {Object.entries(refundDetails.notes).map(([key, value]) => (
                    <div key={key}>
                      <strong>{key}:</strong> {value}
                    </div>
                  ))}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>No refund details found for this payment.</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminPayments;
